import { useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

type Row = Record<string, unknown>;

function humanize(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

function cell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return value.toLocaleString('tr-TR');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Renders an arbitrary report row set as a plain MUI table (no @mui/x-data-grid — pinned
 * TS 4.9.5) with columns inferred from the rows' keys, plus a client-side Excel export
 * via SheetJS. Used by the sales-report pages, whose real column set is unknown.
 */
export function DynamicReportTable({
  rows,
  fileName,
  emptyText = 'Bu kriterlere uygun kayıt bulunamadı.',
}: {
  rows: Row[];
  fileName: string;
  emptyText?: string;
}) {
  const columns = useMemo(() => {
    const seen = new Set<string>();
    for (const row of rows) for (const key of Object.keys(row)) seen.add(key);
    return [...seen];
  }, [rows]);

  const exportExcel = async () => {
    // Dynamic import keeps SheetJS (~400 KB) out of the main bundle — only the
    // admin-only report pages pull it in, and only on first export click.
    const XLSX = await import('xlsx');
    const sheet = XLSX.utils.json_to_sheet(rows);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Rapor');
    XLSX.writeFile(book, `${fileName}.xlsx`);
  };

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {rows.length} kayıt
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={exportExcel}
          disabled={rows.length === 0}
        >
          Excel İndir
        </Button>
      </Stack>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col}>{humanize(col)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={Math.max(1, columns.length)}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {emptyText}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row, index) => (
              <TableRow key={index} hover>
                {columns.map((col) => (
                  <TableCell key={col}>{cell(row[col])}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
